import BusinessProfileIcon from "@material-ui/icons/AccountBalance"
import InvoiceListIcon from "@material-ui/icons/Receipt"
import InventoryListIcon from "@material-ui/icons/ViewList"
import RecordListIcon from "@mui/icons-material/Dashboard"
import BuyerListIcon from "@mui/icons-material/Person"
import SubscriptionIcon from "@mui/icons-material/RequestQuote"
import { Alert, Box, Button, Grid } from "@mui/material"
import dayjs from "dayjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import { FunctionComponent } from "react"
import { FormattedMessage } from "react-intl"
import { Link } from "react-router-dom"
import { Locale } from "../App"
import Color from "../Color"
import { DateLocale, Route, Subscription } from "../Enum"

const buttonStyle = {
  color: Color.Black,
  fontSize: "24px",
  height: "200px",
  margin: "8px",
  marginTop: "16px",
  padding: "8px",
  borderRadius: "25px",
  border: `10px solid ${Color.PrimaryLight85}`,
}

const dateOptions: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "numeric",
  // dateStyle: "full",
  // timeStyle: "full",
}

const Dashboard: FunctionComponent<{
  locale: Locale
  userName: string
  subscriber: boolean
  product: Subscription
}> = ({ locale, userName, subscriber, product }) => {
  const localeDate =
    locale === Locale.Bengali ? DateLocale.Bengali : DateLocale.English
  const analytics = getAnalytics()

  const isTrialSubscription = product === Subscription.TrialSubscription

  return (
    <Box height="90vh" display="flex">
      <Grid container paddingLeft={1} paddingRight={2}>
        <Grid container size={{ xs: 12 }}>
          {isTrialSubscription && (
            <Grid size={{ xs: 12 }}>
              <Alert
                severity="error"
                variant="filled"
                action={
                  <Link
                    to={Route.Subscription}
                    style={{ textDecoration: "none", color: Color.White }}
                    onClick={() => {
                      logEvent(analytics, "web_subscription_visited")
                    }}
                  >
                    <Button color="inherit" size="small">
                      <FormattedMessage id="dashboard.banner.upgrade"></FormattedMessage>
                    </Button>
                  </Link>
                }
              >
                <FormattedMessage id="dashboard.banner.trial"></FormattedMessage>
              </Alert>
            </Grid>
          )}

          <Grid marginTop={isTrialSubscription ? 0 : 5} size={{ xs: 12 }}>
            {/* Welcome user  */}
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bolder",
              }}
            >
              <FormattedMessage id="dashboard.welcome"></FormattedMessage>
              {userName}!
            </div>

            {/* Time */}
            <div
              style={{
                fontSize: "18px",
                color: Color.Gray,
              }}
            >
              <FormattedMessage id="dashboard.time"></FormattedMessage>
              {dayjs().toDate().toLocaleDateString(localeDate, dateOptions)}
            </div>
          </Grid>
        </Grid>

        {/* Visit options cards container */}
        <Grid container spacing={2} size={12}>
          {/* Visit Record list */}
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Link
              to={Route.RecordList}
              style={{ textDecoration: "none", color: Color.Black }}
              onClick={(event) => {
                if (!subscriber) {
                  event.preventDefault()
                } else {
                  logEvent(analytics, "web_record_list_visited")
                }
              }}
            >
              <Button
                fullWidth
                disabled={!subscriber}
                variant="contained"
                style={buttonStyle}
                startIcon={<RecordListIcon />}
              >
                {subscriber && (
                  <FormattedMessage id="dashboard.visit.bookkeeping"></FormattedMessage>
                )}

                {!subscriber && (
                  <FormattedMessage id="dashboard.buy.subscription"></FormattedMessage>
                )}
              </Button>
            </Link>
          </Grid>

          {/* Visit Inventory list */}
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Link
              to={Route.InventoryList}
              style={{ textDecoration: "none", color: Color.Black }}
              onClick={(event) => {
                if (!subscriber) {
                  event.preventDefault()
                } else {
                  logEvent(analytics, "web_inventory_list_visited")
                }
              }}
            >
              <Button
                fullWidth
                disabled={!subscriber}
                variant="contained"
                style={buttonStyle}
                startIcon={<InventoryListIcon />}
              >
                {subscriber && (
                  <FormattedMessage id="dashboard.visit.inventory.list"></FormattedMessage>
                )}

                {!subscriber && (
                  <FormattedMessage id="dashboard.buy.subscription"></FormattedMessage>
                )}
              </Button>
            </Link>
          </Grid>

          {/* Visit Buyer list */}
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Link
              to={Route.BuyerList}
              style={{ textDecoration: "none", color: Color.Black }}
              onClick={(event) => {
                if (!subscriber) {
                  event.preventDefault()
                } else {
                  logEvent(analytics, "web_buyer_list_visited")
                }
              }}
            >
              <Button
                fullWidth
                disabled={!subscriber}
                variant="contained"
                style={buttonStyle}
                startIcon={<BuyerListIcon />}
              >
                {subscriber && (
                  <FormattedMessage id="dashboard.visit.customer.list"></FormattedMessage>
                )}

                {!subscriber && (
                  <FormattedMessage id="dashboard.buy.subscription"></FormattedMessage>
                )}
              </Button>
            </Link>
          </Grid>

          {/* Visit Invoice list */}
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Link
              to={Route.InvoiceList}
              style={{ textDecoration: "none", color: Color.Black }}
              onClick={(event) => {
                if (!subscriber) {
                  event.preventDefault()
                } else {
                  logEvent(analytics, "web_invoice_list_visited")
                }
              }}
            >
              <Button
                fullWidth
                disabled={!subscriber}
                variant="contained"
                style={buttonStyle}
                startIcon={<InvoiceListIcon />}
              >
                {subscriber && (
                  <FormattedMessage id="dashboard.visit.invoice.list"></FormattedMessage>
                )}

                {!subscriber && (
                  <FormattedMessage id="dashboard.buy.subscription"></FormattedMessage>
                )}
              </Button>
            </Link>
          </Grid>

          {/* Visit Business profile list */}
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Link
              to={Route.BusinessProfile}
              style={{ textDecoration: "none", color: Color.Black }}
              onClick={(event) => {
                if (!subscriber) {
                  event.preventDefault()
                } else {
                  logEvent(analytics, "web_business_profile_visited")
                }
              }}
            >
              <Button
                fullWidth
                disabled={!subscriber}
                variant="contained"
                style={buttonStyle}
                startIcon={<BusinessProfileIcon />}
              >
                {subscriber && (
                  <FormattedMessage id="dashboard.visit.business.profile"></FormattedMessage>
                )}

                {!subscriber && (
                  <FormattedMessage id="dashboard.buy.subscription"></FormattedMessage>
                )}
              </Button>
            </Link>
          </Grid>

          {/* Visit subscription */}
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Link
              to={Route.Subscription}
              style={{ textDecoration: "none", color: Color.Black }}
            >
              <Button
                variant="contained"
                fullWidth
                style={buttonStyle}
                startIcon={<SubscriptionIcon />}
                onClick={() => {
                  logEvent(analytics, "web_subscription_visited")
                }}
              >
                <FormattedMessage id="dashboard.visit.subscription"></FormattedMessage>
              </Button>
            </Link>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard
